import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QueryClientProvider } from '@tanstack/react-query'
import { registerQueryClient } from '@/lib/queryClientRef'
import { useAuthStore } from './stores/auth'
import { useConfigStore } from './stores/config'
import { Toaster } from './components/ui/toaster'
import { ConfirmDialogHost } from './components/dialogs/confirm'
import { useConfigLoader } from './hooks/useConfigLoader'
import { useAuthInit } from './hooks/useAuthInit'
import { useBackendHealth } from './hooks/useBackendHealth'
import { useDocumentTitle } from './i18n'
import { ErrorBoundary } from './components/ErrorBoundary'
import { AuthIdentityBoundary } from './components/AuthIdentityBoundary'
import { HealthStatusBanner } from './components/HealthStatusBanner'
import { AppLoading } from './components/AppLoading'
import { ConfigErrorScreen } from './components/ConfigErrorScreen'
import { isNewUser, resolveExplicitDetectionLanguage } from '@/utils/language'
import { SessionEventSubscriber } from './components/session'
import { FabricGuideHost } from '@/lib/fabric/FabricGuideHost'

// Pages
import Login from './pages/Login'
import Register from './pages/Register'
import VerifyEmail from './pages/VerifyEmail'
import SetPassword from './pages/SetPassword'
import Home from './pages/Home'
import Session from './pages/Session'
import Profile from './pages/Profile'
import AdminAnalytics from './pages/AdminAnalytics'
import BookmarkDetail from './pages/BookmarkDetail'
import OAuthCallback from './pages/OAuthCallback'
import OAuthAuthorize from './pages/OAuthAuthorize'
import NotFound from './pages/NotFound'
import DocumentList from './pages/DocumentList'
import Approvals from './pages/Approvals'
import DocumentUpload from './pages/DocumentUpload'
import DocumentDetail from './pages/DocumentDetail'
import Shifts from './pages/Shifts'
import { MAIN_APP_ROUTES } from '@/lib/routes'
import { createAppQueryClient } from '@/lib/queryClient'

// DEV-only design preview harnesses (lazy — excluded from the prod bundle).
const ConversationCardsPreview = lazy(
  () => import('./preview/agent-home/ConversationCardsPreview')
)
const AgentRepresentationsPreview = lazy(
  () => import('./preview/agent-representations/AgentRepresentationsPreview')
)
const HandoverPreview = lazy(() => import('./preview/handover/HandoverPreview'))

const queryClient = createAppQueryClient()

// Expose the client to non-React code (the auth store's identity-boundary
// teardown clears cached PHI on logout / nurse swap). Registered at module load,
// well before any auth action can fire.
registerQueryClient(queryClient)

/**
 * Route guard for the main app.
 * Redirects to /login if not authenticated. The onboarding gate (the first
 * course unfinished) died with the activation run — the training run is
 * tricorder-session's from the cut-over on and has no home yet.
 *
 * Health checks are NOT blocking — the app shell renders immediately, and
 * this guard does not block on config still LOADING either (config was
 * always non-blocking for the already-onboarded main app; only the
 * onboarding flow itself hard-requires real config for its vocabulary).
 * What it DOES gate on: a genuine config FETCH FAILURE. There used to be no
 * gate at all here — GET /config silently substituted a hardcoded
 * FALLBACK_CONFIG on any failure, so the main app just ran on stale
 * defaults with no signal. Now a failure is a real store error
 * (configError), and this route is exactly where that must become visible
 * instead of every downstream `config?.foo` null-check quietly going dark.
 */
export function OnboardedRoute({
  children,
  isInitializing,
  isAuthenticated,
}: {
  children: React.ReactNode
  isInitializing: boolean
  isAuthenticated: boolean
}) {
  const configError = useConfigStore((state) => state.configError)
  const resetConfig = useConfigStore((state) => state.resetConfig)

  // Wait for auth initialization before making redirect decisions
  if (isInitializing) {
    return <AppLoading phase="initializing" />
  }

  // Auth gates before config: an unauthenticated visitor has no business
  // seeing a config-retry screen — send them to /login regardless of
  // whether GET /config happened to fail. ConfigErrorScreen is only
  // meaningful once someone is actually signed in.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (configError) {
    return <ConfigErrorScreen onRetry={resetConfig} />
  }

  // The onboarding gate this guard used to enforce died with the activation
  // run (the training run is tricorder-session's from the cut-over on and has
  // no home yet). Everyone lands on home.
  return <>{children}</>
}

/**
 * Route guard for the onboarding page.
 * Redirects to /home if already onboarded.
 * Waits for config to load from BACKEND before rendering (real config is
 * required here — e.g. the deployment's actual assistant-name options).
 *
 * If the config fetch genuinely fails (network or schema drift), there is
 * no fallback to fall back to: this auto-retries with backoff up to
 * MAX_RETRIES, then hands control to the user via ConfigErrorScreen. It
 * used to detect a "fallback" configSource and retry toward getting REAL
 * config instead of the hardcoded stand-in; now that the stand-in is gone,
 * the same retry rhythm targets `configError` directly.
 */
export default function App() {
  const { i18n } = useTranslation()

  // The logged-in user's profile language is the source of truth for the UI
  // locale: override browser detection once the user loads, so the shell
  // matches the language the backend renders agent content in.
  const userLanguage = useAuthStore((state) => state.user?.language)
  useEffect(() => {
    if (userLanguage && userLanguage !== i18n.language) {
      void i18n.changeLanguage(userLanguage)
    }
  }, [userLanguage, i18n])

  // Load app config on mount
  useConfigLoader();

  // config.language.detection: "explicit" corrects a brand-new visitor's
  // i18n language to the deployment's own default, undoing the one-time
  // browser-sniffed guess i18n/index.ts's module-level init made before
  // config was even requested. See resolveExplicitDetectionLanguage's doc
  // comment for the full ordering constraint and why a real choice (a
  // logged-in profile language, or a stored preference) is never touched.
  const configLanguage = useConfigStore((state) => state.config?.language)
  useEffect(() => {
    if (!configLanguage) return
    const next = resolveExplicitDetectionLanguage({
      detection: configLanguage.detection,
      configDefault: configLanguage.default,
      userLanguage,
      hasStoredPreference: !isNewUser(),
      currentLanguage: i18n.language,
    })
    if (next) void i18n.changeLanguage(next)
  }, [configLanguage, userLanguage, i18n])

  // Update document title based on language
  useDocumentTitle();

  // Sync document lang attribute with i18n locale for screen readers and search engines
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // Refetch stale queries when app returns from background (iOS Safari PWA suspend/resume).
  // Only invalidates queries past their staleTime — preserves fresh cache for instant UI.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void queryClient.invalidateQueries({
          predicate: (query) => query.isStale(),
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Check backend health on startup (non-blocking — banner shows status)
  const { isHealthy, isStarting, isChecking, error: healthError, checkHealth } = useBackendHealth();

  // Restore auth session from httpOnly cookie on startup
  // IMPORTANT: Use isAuthenticated from useAuthInit, not from store directly,
  // because store may not be rehydrated yet (Safari is slower than Chrome)
  const { isInitializing, isAuthenticated } = useAuthInit();

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* Non-blocking health banner — visible for authenticated users while backend is starting/down */}
        {isAuthenticated && (
          <HealthStatusBanner
            isBackendHealthy={isHealthy}
            isBackendStarting={isStarting}
            isHealthChecking={isChecking}
            error={healthError}
            onRetry={checkHealth}
          />
        )}
        {/* Remount the routed view on an identity change (a shared-device nurse
            swap resolved in place via setAuth, no reload) so component-local state
            — open dialogs, streaming buffers — never carries the outgoing nurse's
            context into the next session. Same-user refresh keeps the id → no
            remount. Complements the auth store's module-level teardown. #2150 */}
        <AuthIdentityBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/auth/callback/:provider" element={<OAuthCallback />} />
          {/* OAuth 2.1 AS consent (MCP connectors): api-service bounces the
              connector's authorize GET here; the component handles login
              (via /login?returnTo) + consent, then follows the server-built
              redirect_url back to the connector. */}
          <Route
            path="/oauth/authorize"
            element={<OAuthAuthorize isInitializing={isInitializing} isAuthenticated={isAuthenticated} />}
          />
          {/* /home/* — the pane hosts each agent's own route tree under
              /home/a/<slug>/… (AgentFrame). */}
          <Route path="/home/*" element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><Home /></OnboardedRoute>} />
          <Route path="/sessions/:id" element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><Session /></OnboardedRoute>} />
          <Route path="/profile" element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><Profile /></OnboardedRoute>} />
          <Route path="/shifts" element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><Shifts /></OnboardedRoute>} />
          <Route path="/approvals" element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><Approvals /></OnboardedRoute>} />
          <Route path={MAIN_APP_ROUTES.documents} element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><DocumentList /></OnboardedRoute>} />
          <Route path={`${MAIN_APP_ROUTES.documents}/upload`} element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><DocumentUpload /></OnboardedRoute>} />
          <Route path={`${MAIN_APP_ROUTES.documents}/:id`} element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><DocumentDetail /></OnboardedRoute>} />
          {/* Deprecated: document-chat routes redirect to regular chat */}
          <Route path="/document-chat" element={<Navigate to="/home" replace />} />
          <Route path="/document-chat/:id" element={<Navigate to="/home" replace />} />
          <Route path={MAIN_APP_ROUTES.adminAnalytics} element={<AdminAnalytics />} />
          <Route path="/bookmarks/:bookmarkId" element={<OnboardedRoute isInitializing={isInitializing} isAuthenticated={isAuthenticated}><BookmarkDetail /></OnboardedRoute>} />
          <Route path="/" element={<Navigate to="/home" replace />} />
          {import.meta.env.DEV && (
            <>
              <Route
                path="/preview/conversation-cards"
                element={
                  <Suspense fallback={null}>
                    <ConversationCardsPreview />
                  </Suspense>
                }
              />
              <Route
                path="/preview/agent-representations"
                element={
                  <Suspense fallback={null}>
                    <AgentRepresentationsPreview />
                  </Suspense>
                }
              />
              <Route
                path="/preview/handover"
                element={
                  <Suspense fallback={null}>
                    <HandoverPreview />
                  </Suspense>
                }
              />
            </>
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </AuthIdentityBoundary>
        <SessionEventSubscriber />
        <FabricGuideHost />
        <Toaster />
        <ConfirmDialogHost />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
